import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play,
  Pause,
  Maximize2,
  Eye,
  MessageSquare,
  ShieldCheck,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

const features = [
  {
    icon: Eye,
    title: "Real-Time Build Visibility",
    description:
      "Track commits, pull requests, and release progress directly from the portal. Always know where your desktop app and platform stand, without status meetings.",
  },
  {
    icon: MessageSquare,
    title: "Direct-to-Engineer Connection",
    description:
      "One senior engineer builds your entire stack, inside your own AWS account and repos. Direct, secure collaboration on architecture, features, and release decisions, with no account managers in between.",
  },
  {
    icon: ShieldCheck,
    title: "Data Isolation and Zero-Knowledge Billing",
    description:
      "Contracts executed via DocuSign, stored with admin-only access on AWS S3. Invoicing and PII handled entirely through Stripe, with no sensitive data on our servers.",
  },
];

export function ClientPortalExperience() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [theaterOpen, setTheaterOpen] = useState(false);
  const [theaterPlaying, setTheaterPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const theaterVideoRef = useRef<HTMLVideoElement>(null);
  const cdnUrl = import.meta.env.VITE_CDN_URL;
  const videoSrc = `${cdnUrl}/content/branding/clientPortalExpirence.mp4`;

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const openTheater = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
    setTheaterOpen(true);
  }, []);

  const closeTheater = useCallback(() => {
    if (theaterVideoRef.current) {
      theaterVideoRef.current.pause();
    }
    setTheaterPlaying(false);
    setTheaterOpen(false);
  }, []);

  const toggleTheaterPlay = () => {
    if (!theaterVideoRef.current) return;
    if (theaterPlaying) {
      theaterVideoRef.current.pause();
    } else {
      theaterVideoRef.current.play();
    }
    setTheaterPlaying(!theaterPlaying);
  };

  useEffect(() => {
    if (!theaterOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeTheater();
      if (e.key === " ") {
        e.preventDefault();
        toggleTheaterPlay();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  });

  useEffect(() => {
    if (theaterOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [theaterOpen]);

  return (
    <>
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/[0.02] to-background" />

        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 1400 800"
          fill="none"
          preserveAspectRatio="xMidYMid slice"
        >
          {[...Array(6)].map((_, i) => (
            <line
              key={i}
              x1={200 + i * 200}
              y1="0"
              x2={200 + i * 200}
              y2="800"
              stroke="hsl(30 95% 55%)"
              strokeWidth="0.3"
              opacity="0.04"
            />
          ))}
          {[...Array(4)].map((_, i) => (
            <line
              key={`h-${i}`}
              x1="0"
              y1={200 + i * 200}
              x2="1400"
              y2={200 + i * 200}
              stroke="hsl(30 95% 55%)"
              strokeWidth="0.3"
              opacity="0.03"
            />
          ))}
        </svg>

        <div className="absolute top-[30%] right-[5%] w-[400px] h-[400px] bg-primary/[0.04] rounded-full blur-[120px]" />
        <div className="absolute bottom-[20%] left-[10%] w-[300px] h-[300px] bg-accent/[0.03] rounded-full blur-[100px]" />

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge variant="info" className="mb-4">
              Founder Portal
            </Badge>
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">
              Build in the Open,{" "}
              <span className="text-gradient">Ship with Confidence</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              See exactly what your engagement looks like from day one: desktop
              builds, platform deploys, and release milestones in one place.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative group"
            >
              <div className="relative rounded-2xl overflow-hidden border border-white/[0.08] shadow-2xl shadow-primary/[0.08]">
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/[0.05] via-transparent to-accent/[0.03] z-10 pointer-events-none" />

                <video
                  ref={videoRef}
                  src={videoSrc}
                  poster={`${cdnUrl}/content/branding/clientPortalExpirencePreview.png`}
                  className="w-full aspect-video object-cover"
                  playsInline
                  onEnded={() => setIsPlaying(false)}
                />

                <button
                  onClick={togglePlay}
                  className="absolute inset-0 z-20 flex items-center justify-center"
                >
                  <motion.div
                    initial={false}
                    animate={{
                      scale: isPlaying ? 0.9 : 1,
                      opacity: isPlaying ? 0 : 1,
                    }}
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.2 }}
                    className="w-20 h-20 rounded-full bg-primary/90 backdrop-blur-sm flex items-center justify-center shadow-lg shadow-primary/30 group-hover:bg-primary transition-colors"
                  >
                    {isPlaying ? (
                      <Pause className="h-8 w-8 text-primary-foreground" />
                    ) : (
                      <Play className="h-8 w-8 text-primary-foreground ml-1" />
                    )}
                  </motion.div>
                </button>

                <button
                  onClick={openTheater}
                  className="absolute top-3 right-3 z-30 w-9 h-9 rounded-lg bg-black/50 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>

                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
              </div>

              <div className="absolute -inset-px rounded-2xl bg-gradient-to-tr from-primary/20 via-transparent to-accent/10 -z-10 blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + index * 0.15 }}
                  className="group/item flex gap-5"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover/item:bg-primary/20 transition-colors">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-lg mb-1.5">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {theaterOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-md"
            onClick={closeTheater}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="relative w-full max-w-6xl mx-6"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeTheater}
                className="absolute -top-12 right-0 flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
              >
                <span>Close</span>
                <X className="h-5 w-5" />
              </button>

              <div className="relative rounded-xl overflow-hidden border border-white/10 shadow-2xl shadow-primary/10">
                <video
                  ref={theaterVideoRef}
                  src={videoSrc}
                  className="w-full aspect-video"
                  playsInline
                  controls={theaterPlaying}
                  autoPlay
                  onPlay={() => setTheaterPlaying(true)}
                  onPause={() => setTheaterPlaying(false)}
                  onEnded={() => setTheaterPlaying(false)}
                />

                {!theaterPlaying && (
                  <button
                    onClick={toggleTheaterPlay}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="w-24 h-24 rounded-full bg-primary/90 backdrop-blur-sm flex items-center justify-center shadow-lg shadow-primary/30 hover:bg-primary transition-colors"
                    >
                      <Play className="h-10 w-10 text-primary-foreground ml-1" />
                    </motion.div>
                  </button>
                )}

                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
              </div>

              <div className="absolute -inset-1 rounded-xl bg-gradient-to-tr from-primary/10 via-transparent to-accent/5 -z-10 blur-md" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
